import Http, { ApiHttpResponse } from "@/services/Http";
import Emitter from "@/services/Emitter";
import Cache from "@/services/Cache";
import Helper, { DateFormat } from "@/services/Helper";


import { TaskClockModel } from "@/models/TaskClockModel";
import { ContactModel, ClientModel } from "@/models/ClientModel";
import { RequestModel, JobModel, RequestFilter} from "@/models/RequestModel";
import { TimeModel, TimeModelFilter, TaskModel } from "@/models/TaskModel";
import { MileageModel, MileageModelFilter } from "@/models/MileageModel";
import { PartModel, PartModelFilter } from "@/models/PartModel";
import { PMModel, PMModelFilter } from "@/models/PMModel";
import { NoteModel, TaskNoteModel } from "@/models/TaskNoteModel";
import { ExpenseModel, ExpenseModelFilter } from "@/models/ExpenseModel";
import { TechnicianModel } from "@/models/TechnicianModel";
import { OrderStatusModel } from "@/models/OrderStatusModel";
import { PriorityModel } from "@/models/PriorityModel";
import { WorkOrderModel, WorkOrderFilter } from "@/models/WorkOrderModel";
import { WorkOrderItemModel, WorkOrderItemFilter } from "@/models/WorkOrderItemModel";

// REPLACED ...
// import ns from "@/services/Api";

// Generic API Response interface
export interface ApiResponse<T = any> {
    Status: number;
    Message: string;
    Redirect: string;
    Meta: {
        Total?: number;
        Filter?: any;
    };
    Data: T;
}

// Helper function to create ApiResponse for methods that don't return full response
const createApiResponse = <T>(data: T, total?: number): ApiResponse<T> => ({
    Status: 1,
    Message: "",
    Redirect: "",
    Meta: {
        Total: total,
        Filter: {}
    },
    Data: data
});

const startSpin = (bg:boolean | undefined): void =>
{
    if (!bg)
    {
        Emitter.emit("loading", true);
    }
};

const stopSpin = (bg:boolean | undefined): void =>
{
    if (!bg)
    {
        Emitter.emit("loading", false);
    }
}

class ApiService
{


    //region Customers

    // TODO - change to loadAllClients and return array vs loadClients returns the raw response with filters etc.
    public async loadClients (bg?:boolean): Promise<ClientModel[]>
    {
        startSpin(bg);
        let rs = await Http.get("/company/list?page=0&size=999999&v=2");
        for (let i = 0; i < rs.length; i++)
        {
            rs[i] = new ClientModel(rs[i]);
        }
        stopSpin(bg);
        return rs;
    }

    public async loadClient (id:number | string, bg?:boolean): Promise<ClientModel> 
    {
        startSpin(bg);
        let rs = await Http.get("/company/details/" + id + "?v=2");
        rs = new ClientModel(rs);
        stopSpin(bg);
        return rs;
    };

    public async loadClientByCustomerNumber (cust:number | string, bg?:boolean): Promise<ClientModel>
    {
        startSpin(bg);
        let rs = await Http.get("/company/list?v=2", {
            CustomerNumber: cust
        });
        rs = new ClientModel(rs.length ? rs[0] : {});
        stopSpin(bg);
        return rs;
    }

    public async saveClient (client:ClientModel, bg?:boolean): Promise<ClientModel> 
    {
        startSpin(bg);
        let data = Helper.prep(client);
        let rs = await Http.post("/company/save?v=2", data);
        rs = new ClientModel(rs);
        stopSpin(bg);
        return rs;
    }

    //endregion


    //region Clocks
    public async loadClocks (bg?: boolean): Promise<TaskModel[]>
    {
        startSpin(bg);
        let list = await Http.get("/TaskClock/List?v=2") || [];
        
        for (let i = 0; i < list.length; i++)
        {
            list[i] = new TaskModel(list[i]);
        }
        
        stopSpin(bg);
        return list;
    }

    public async loadClock (id: number | string, bg?: boolean): Promise<TaskModel>
    {
        startSpin(bg);
        let data = await Http.get("/TaskClock/Details/" + id + "?v=2");
        data = new TaskModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveClock (clock: TaskClockModel, bg?: boolean): Promise<TaskClockModel>
    {
        let data = Helper.prep(clock);
        startSpin(bg);
        data = await Http.post("/TaskClock/Save?v=2", data) || {};
        stopSpin(bg);
        return new TaskClockModel(data);
    };

    //#endregion


    //#region Requests

    public async loadRequests (filter: RequestFilter, bg?: boolean): Promise<ApiHttpResponse<RequestModel[]>>
    {
        startSpin(bg);
        
        // make sure certain items are set in the filter
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<RequestModel[]>("/Request/List?v=2", filter);
        
        console.log('Raw API response:', response);
        
        // Process the data array and instantiate RequestModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new RequestModel(response.Data[i]);
            }
        }
        
        console.log('Final processed response:', response);
        
        stopSpin(bg);
        return response;
    }

    public async loadJobStatus (bg?: boolean) 
    {
        startSpin(bg);
        let rs = await Http.get("/JobStatus/List?v=2");
        stopSpin(bg);
        return rs;
    }

    public async loadOrderStatus (bg?: boolean): Promise<ApiResponse<OrderStatusModel[]>>
    {
        startSpin(bg);
        
        let response = await Http.getFullResponse<OrderStatusModel[]>("/OrderStatus/List?v=2");
        
        // Process the data array and instantiate OrderStatusModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new OrderStatusModel(response.Data[i]);
            }
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadPriorities (bg?: boolean): Promise<ApiResponse<PriorityModel[]>>
    {
        startSpin(bg);
        
        let response = await Http.getFullResponse<PriorityModel[]>("/Priority/List?v=2");
        
        // Process the data array and instantiate PriorityModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new PriorityModel(response.Data[i]);
            }
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadRequest (id: number | string, bg?: boolean): Promise<RequestModel>
    {
        startSpin(bg);
        let data = await Http.get("/Request/Details/" + id + "?v=2");
        data = new RequestModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveRequest (request: RequestModel, bg?: boolean): Promise<RequestModel>
    {
        let data = Helper.prep(request);
        startSpin(bg);
        data = await Http.post("/Request/Save?v=2", data) || {};
        stopSpin(bg);
        return new RequestModel(data);
    };

    public async loadClosedStatuses (bg?: boolean): Promise<string[]>
    {
        startSpin(bg);
        const response = await Http.get("/Request/ClosedStatuses?v=2");
        stopSpin(bg);
        return response || [];
    }

    //#endregion

    //#region Time

    public async loadTimes (filter: TimeModelFilter, bg?: boolean): Promise<ApiResponse<TimeModel[]>>
    {
        startSpin(bg);
        
        let response = await Http.getFullResponse<TimeModel[]>("/Time/List?v=2", filter);
        
        console.log('Raw API response:', response);
        
        // Process the data array and instantiate TimeModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new TimeModel(response.Data[i]);
            }
        }
        
        console.log('Final processed response:', response);
        
        stopSpin(bg);
        return response;
    }

    public async loadTime (id: number | string, bg?: boolean): Promise<TimeModel>
    {
        startSpin(bg);
        let data = await Http.get("/Time/Details/" + id + "?v=2");
        data = new TimeModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveTime (time: TimeModel, bg?: boolean): Promise<TimeModel>
    {
        let data = Helper.prep(time);
        startSpin(bg);
        data = await Http.post("/Time/Save?v=2", data) || {};
        stopSpin(bg);
        return new TimeModel(data as TaskModel);
    };

    //#endregin

    //#region Mileage

    public async loadMileages (filter: MileageModelFilter, bg?: boolean): Promise<ApiResponse<MileageModel[]>>
    {
        startSpin(bg);
        console.log('loadMileages called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<MileageModel[]>("/Mileage/List?v=2", filter);
        console.log('loadMileages response:', response);
        
        // Process the Data array to instantiate MileageModel objects
        const list = (response.Data || []).map(item => new MileageModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    }

    public async loadMileage (id: number | string, bg?: boolean): Promise<MileageModel>
    {
        startSpin(bg);
        let data = await Http.get("/Mileage/Details/" + id + "?v=2");
        data = new MileageModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveMileage (mileage: MileageModel, bg?: boolean): Promise<MileageModel>
    {
        let data = Helper.prep(mileage);
        startSpin(bg);
        data = await Http.post("/Mileage/Save?v=2", data) || {};
        stopSpin(bg);
        return new MileageModel(data);
    };

    //#endregion


    //#region Parts

    public async loadParts (filter: PartModelFilter, bg?: boolean): Promise<ApiResponse<PartModel[]>>
    {
        startSpin(bg);
        
        // make sure certain items are set in the filter
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<PartModel[]>("/Part/List?v=2", filter);
        
        console.log('Raw API response:', response);
        
        // Process the data array and instantiate PartModel objects
        if (response.Data && Array.isArray(response.Data)) {
            for (let i = 0; i < response.Data.length; i++) {
                response.Data[i] = new PartModel(response.Data[i]);
            }
        }
        
        console.log('Final processed response:', response);
        
        stopSpin(bg);
        return response;
    }

    public async loadPart (id: number | string, bg?: boolean): Promise<PartModel>
    {
        startSpin(bg);
        let data = await Http.get("/Part/Details/" + id + "?v=2");
        data = new PartModel(data);
        stopSpin(bg);
        return data;
    }

    public async savePart (part: PartModel, bg?: boolean): Promise<PartModel>
    {
        let data = Helper.prep(part);
        startSpin(bg);
        data = await Http.post("/Part/Save?v=2", data) || {};
        stopSpin(bg);
        return new PartModel(data);
    };

    //#endregion


    //#region Expenses

    public async loadExpenses (filter:ExpenseModelFilter, bg:boolean | undefined): Promise<ApiResponse<ExpenseModel[]>>
    {
        startSpin(bg);
        console.log('loadExpenses called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<ExpenseModel[]>("/Expense/List?v=2", filter);
        console.log('loadExpenses response:', response);
        
        // Process the Data array to instantiate ExpenseModel objects
        const list = (response.Data || []).map(item => new ExpenseModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    };

    public async loadExpense (id:number | string, bg?:boolean | undefined): Promise<ExpenseModel>
    {
        startSpin(bg);
        let expense:ExpenseModel = await Http.get("/Expense/Details/" + id + "?v=2") || {} as ExpenseModel;
        stopSpin(bg);
        return new ExpenseModel(expense);
    };

    public async saveExpense (expense:ExpenseModel, bg?:boolean | undefined): Promise<ExpenseModel>
    {
        let data = Helper.prep(expense);
        console.debug("save expense", data);
        
        startSpin(bg);
        data = await Http.post("/Expense/Save?v=2", data) || {};
        stopSpin(bg);
        return new ExpenseModel(data);
    };

    //#endregion


    //#region PMs

    public async loadPMs (filter: PMModelFilter, bg?: boolean): Promise<ApiResponse<PMModel[]>>
    {
        startSpin(bg);
        console.log('loadPMs called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeContact = true;
        filter.IncludeTechnician = true;
        filter.IncludeLinkedRequest = true;
        
        let response = await Http.getFullResponse<PMModel[]>("/PM/List?v=2", filter);
        console.log('loadPMs response:', response);
        
        // Process the Data array to instantiate PMModel objects
        const list = (response.Data || []).map(item => new PMModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    }

    public async loadPM (id: number | string, bg?: boolean): Promise<PMModel>
    {
        startSpin(bg);
        let pm = await Http.get("/PM/Details/" + id + "?v=2") || {} as PMModel;
        stopSpin(bg);
        return new PMModel(pm);
    }

    public async loadPMByRequest (requestId: number | string, bg?: boolean): Promise<PMModel | null>
    {
        startSpin(bg);
        let pm = await Http.get("/PM/ByRequest/" + requestId + "?v=2");
        stopSpin(bg);
        return pm ? new PMModel(pm) : null;
    }

    public async savePM (pm: PMModel, bg?: boolean): Promise<PMModel>
    {
        let data = Helper.prep(pm);
        console.debug("save PM", data);
        
        startSpin(bg);
        data = await Http.post("/PM/Save?v=2", data) || {};
        stopSpin(bg);
        return new PMModel(data);
    }

    public async deletePM (id: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get("/PM/Delete/" + id + "?v=2");
        stopSpin(bg);
        return response;
    }

    //#endregion

    //#region Notes

    public async loadNotes (filter: any, bg?: boolean): Promise<ApiResponse<TaskNoteModel[]>>
    {
        startSpin(bg);
        console.log('loadNotes called with filter:', filter);
        
        // Set required filter properties
        filter.IncludeRequest = true;
        filter.IncludeContact = true;
        
        let response = await Http.getFullResponse<TaskNoteModel[]>("/TaskNote/List?v=2", filter);
        console.log('loadNotes response:', response);
        
        // Process the Data array to instantiate TaskNoteModel objects
        const list = (response.Data || []).map(item => new NoteModel(item));
        response.Data = list;
        
        stopSpin(bg);
        return response;
    }

    public async loadNote (id: Number, bg?: boolean): Promise<TaskNoteModel>
    {
        startSpin(bg);
        let data = await Http.get("/TaskNote/Details/" + id + "?v=2");
        data = new NoteModel(data);
        stopSpin(bg);
        return data;
    }

    public async saveNote (note:TaskNoteModel, bg?: boolean): Promise<TaskNoteModel>
    {
        startSpin(bg);
        let data = Helper.prep(note);
        data = await Http.post("/TaskNote/Save?v=2", note);
        stopSpin(bg);
        return new NoteModel(data);
    };

    //#endregion

    //#region Techs

    public async loadTech (id:number, bg?:boolean): Promise<TechnicianModel>
    {
        startSpin(bg);
        let rs = await Http.get("/user/details/" + id + "?v=2");
        rs.Email = rs.Credential.Key;
        let tech = new TechnicianModel(rs);
        stopSpin(bg);
        return tech;
    }

    public async loadTechs (bg?:boolean) :Promise<Array<TechnicianModel>>
    {
        startSpin(bg);
        let rs = await Http.get("/user/list?page=0&size=999999&v=2");

        for (let i = 0; i < rs.length; i++)
        {
            rs[i] = new TechnicianModel(rs[i]);
            rs[i].Email = rs[i].Credential.Key;
        }

        stopSpin(bg);
        return rs;
    }

    public async saveTech (tech:TechnicianModel, bg?:boolean): Promise<TechnicianModel>
    {
        startSpin(bg);
        let data = Helper.prep(tech);
        data = await Http.post("/Technician/Save?v=2", data) || {};
        stopSpin(bg);
        return new TechnicianModel(data);   
    }

    // #endregion

    //#region Profile Images

    public async loadContactImage(contactId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get("/Data/ContactImage/" + contactId + "?v=2");
        stopSpin(bg);
        return response?.Data || null;
    }

    public async loadTechnicianImage(techId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.get("/Data/TechnicianImage/" + techId + "?v=2");
        stopSpin(bg);
        return response || null;
    }

    public async uploadProfileImage(file: File, type: string, entityId: number, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        
        const formData = new FormData();
        formData.append('File', file);
        formData.append('Type', type);
        
        if (type === 'contact') {
            formData.append('ClientId', entityId.toString());
        } else if (type === 'technician') {
            formData.append('OwnerId', entityId.toString());
        }
        
        let response = await Http.upload("/Data/Upload?v=2", formData);
        stopSpin(bg);
        return response?.Data || null;
    }

    public async deleteProfileImage(imageId: string, bg?: boolean): Promise<any>
    {
        startSpin(bg);
        let response = await Http.post("/Data/Delete/" + imageId + "?v=2", {});
        stopSpin(bg);
        return response?.Data || null;
    }

    //#endregion

    //#region WorkOrders

    public async loadWorkOrders(filter?: WorkOrderFilter, bg?: boolean): Promise<ApiHttpResponse<WorkOrderModel[]>>
    {
        startSpin(bg);
        // const params = filter ? Helper.prep(filter) : {};

        const params = filter || {};

        let response = await Http.getFullResponse<WorkOrderModel[]>("/WorkOrder/List?v=2", params);
        
        if (response.Data && Array.isArray(response.Data))
        {
            response.Data = response.Data.map((item: any) => new WorkOrderModel(item));
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadWorkOrder(id: number | string, bg?: boolean): Promise<WorkOrderModel>
    {
        startSpin(bg);
        let rs = await Http.get("/WorkOrder/Details/" + id + "?v=2");
        rs = new WorkOrderModel(rs);
        stopSpin(bg);
        return rs;
    }

    public async saveWorkOrder(data: WorkOrderModel, bg?: boolean): Promise<WorkOrderModel>
    {
        startSpin(bg);
        let rs = await Http.post("/WorkOrder/Save?v=2", Helper.prep(data));
        rs = new WorkOrderModel(rs);
        stopSpin(bg);
        return rs;
    }

    public async deleteWorkOrder(id: number, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        let response = await Http.get("/WorkOrder/Delete/" + id + "?v=2");
        stopSpin(bg);
        return response?.Status === 1;
    }

    //#endregion

    //#region WorkOrderItems

    public async loadWorkOrderItems(filter?: WorkOrderItemFilter, bg?: boolean): Promise<ApiHttpResponse<WorkOrderItemModel[]>>
    {
        startSpin(bg);
        const params = filter;  // ? Helper.prep(filter) : {};
        let response = await Http.getFullResponse<WorkOrderItemModel[]>("/WorkOrderItem/List?v=2", params);
        
        if (response.Data && Array.isArray(response.Data))
        {
            response.Data = response.Data.map((item: any) => new WorkOrderItemModel(item));
        }
        
        stopSpin(bg);
        return response;
    }

    public async loadWorkOrderItem(id: number | string, bg?: boolean): Promise<WorkOrderItemModel>
    {
        startSpin(bg);
        let rs = await Http.get("/WorkOrderItem/Details/" + id + "?v=2");
        rs = new WorkOrderItemModel(rs);
        stopSpin(bg);
        return rs;
    }

    public async saveWorkOrderItem(data: WorkOrderItemModel, bg?: boolean): Promise<WorkOrderItemModel>
    {
        startSpin(bg);
        let rs = await Http.post("/WorkOrderItem/Save?v=2", Helper.prep(data));
        rs = new WorkOrderItemModel(rs);
        stopSpin(bg);
        return rs;
    }

    public async deleteWorkOrderItem(id: number, bg?: boolean): Promise<boolean>
    {
        startSpin(bg);
        let response = await Http.post("/WorkOrderItem/Delete/" + id);
        stopSpin(bg);
        return response?.Status === 1;
    }

    //#endregion
}


const singleton = new ApiService();
export default singleton;